import * as React from 'react';

export type EntityType = 'fixture' | 'circuit' | 'control' | 'panel' | 'room';

interface SelectionContextValue {
  selectedFixtures: Set<string>;
  selectedCircuits: Set<string>;
  selectedControls: Set<string>;
  selectedPanels: Set<string>;
  selectedRooms: Set<string>;

  selectFixture: (id: string) => void;
  toggleFixture: (id: string) => void;
  clearFixtureSelection: () => void;

  selectCircuit: (id: string) => void;
  toggleCircuit: (id: string) => void;
  clearCircuitSelection: () => void;

  selectControl: (id: string) => void;
  toggleControl: (id: string) => void;
  clearControlSelection: () => void;

  selectPanel: (id: string) => void;
  togglePanel: (id: string) => void;
  clearPanelSelection: () => void;

  selectRoom: (id: string) => void;
  toggleRoom: (id: string) => void;
  clearRoomSelection: () => void;

  clearAllSelections: () => void;

  hoveredEntity: { type: EntityType; id: string } | null;
  setHoveredEntity: (type: EntityType, id: string | null) => void;
  clearHoveredEntity: () => void;

  setSelectedFixtures: React.Dispatch<React.SetStateAction<Set<string>>>;
  setSelectedControls: React.Dispatch<React.SetStateAction<Set<string>>>;
  add: (item: { type: EntityType; id: string }, multi?: boolean) => void;
  toggle: (item: { type: EntityType; id: string }, multi?: boolean) => void;
  clear: () => void;
}

const SelectionContext = React.createContext<SelectionContextValue | undefined>(undefined);

function useEntitySelection(initial: Set<string> = new Set()) {
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(initial);

  const selectOne = React.useCallback((id: string) => setSelectedIds(new Set([id])), []);
  const toggleSelection = React.useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);
  const clear = React.useCallback(() => setSelectedIds(new Set()), []);

  // extra helpers used by legacy selection manager
  const add = React.useCallback((id: string, multi = false) => {
    setSelectedIds(prev => {
      const next: Set<string> = multi ? new Set(prev) : new Set<string>();
      next.add(id);
      return next;
    });
  }, []);

  const remove = React.useCallback((id: string) => {
    setSelectedIds(prev => {
      if (!prev.has(id)) return prev;
      const next: Set<string> = new Set(prev);
      next.delete(id);
      return next;
    });
  }, []);

  return { selectedIds, selectOne, toggleSelection, clear, add, remove, setSelectedIds } as const;
}

export function SelectionProvider({ children }: { children: React.ReactNode }) {
  const fixturesSel = useEntitySelection();
  const circuitsSel = useEntitySelection();
  const controlsSel = useEntitySelection();
  const panelsSel = useEntitySelection();
  const roomsSel = useEntitySelection();

  const [hoveredEntity, setHoveredEntityState] = React.useState<{
    type: EntityType;
    id: string;
  } | null>(null);

  const setHoveredEntity = React.useCallback((type: EntityType, id: string | null) => {
    setHoveredEntityState(id ? { type, id } : null);
  }, []);

  const clearHoveredEntity = React.useCallback(() => setHoveredEntityState(null), []);

  const clearAllSelections = React.useCallback(() => {
    fixturesSel.clear();
    circuitsSel.clear();
    controlsSel.clear();
    panelsSel.clear();
    roomsSel.clear();
  }, [fixturesSel, circuitsSel, controlsSel, panelsSel, roomsSel]);

  const value = React.useMemo<SelectionContextValue>(
    () => ({
      // selections
      selectedFixtures: fixturesSel.selectedIds,
      selectedCircuits: circuitsSel.selectedIds,
      selectedControls: controlsSel.selectedIds,
      selectedPanels: panelsSel.selectedIds,
      selectedRooms: roomsSel.selectedIds,
      // fixture helpers
      selectFixture: fixturesSel.selectOne,
      toggleFixture: fixturesSel.toggleSelection,
      clearFixtureSelection: fixturesSel.clear,
      // circuit helpers
      selectCircuit: circuitsSel.selectOne,
      toggleCircuit: circuitsSel.toggleSelection,
      clearCircuitSelection: circuitsSel.clear,
      // control helpers
      selectControl: controlsSel.selectOne,
      toggleControl: controlsSel.toggleSelection,
      clearControlSelection: controlsSel.clear,
      // panel helpers
      selectPanel: panelsSel.selectOne,
      togglePanel: panelsSel.toggleSelection,
      clearPanelSelection: panelsSel.clear,
      // room helpers
      selectRoom: roomsSel.selectOne,
      toggleRoom: roomsSel.toggleSelection,
      clearRoomSelection: roomsSel.clear,
      // Legacy-manager compatibility setters
      setSelectedFixtures: fixturesSel.setSelectedIds,
      setSelectedControls: controlsSel.setSelectedIds,
      // Selection helpers from legacy API
      add: (item, multi = false) => {
        if (item.type === 'fixture') {
          fixturesSel.add(item.id, multi);
        } else if (item.type === 'control') {
          controlsSel.add(item.id, multi);
        }
      },
      toggle: (item, multi = false) => {
        if (item.type === 'fixture') {
          fixturesSel.toggleSelection(item.id);
        } else if (item.type === 'control') {
          controlsSel.toggleSelection(item.id);
        }
      },
      clear: () => {
        fixturesSel.clear();
        controlsSel.clear();
      },
      // Global operations
      clearAllSelections,
      hoveredEntity,
      setHoveredEntity,
      clearHoveredEntity,
    }),
    [
      fixturesSel,
      circuitsSel,
      controlsSel,
      panelsSel,
      roomsSel,
      hoveredEntity,
      setHoveredEntity,
      clearHoveredEntity,
      clearAllSelections,
    ],
  );

  return <SelectionContext.Provider value={value}>{children}</SelectionContext.Provider>;
}

export function useSelection() {
  const ctx = React.useContext(SelectionContext);
  if (!ctx) throw new Error('useSelection must be used within SelectionProvider');
  return ctx;
}
